import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { ArticleDetailScreen } from '@/common/ArticleDetailScreen';
import { NotFoundScreen } from '@/common/ErrorScreen';

/**
 * The legacy address for a student news single. WordPress served these at
 * `/student-news/<slug>`, so the URL is kept working rather than 404'd on every
 * link that already exists in the wild; `/news/<slug>` is the canonical route
 * and `news.$slug.tsx` is its twin.
 *
 * The kind comes from the URL rather than the viewer's role, so an old link
 * resolves to the same article whoever opens it.
 */
function StudentNewsPost(): ReactElement | null {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { data, isError, isPending } = api.getStudentNewsPost.useQuery({
    path: { slug },
  });

  if (isError) return <NotFoundScreen />;
  // `data` is undefined for the whole in-flight window and
  // `ArticleDetailScreen` prints `notFoundMessage` for a falsy article, so an
  // unguarded hand-off flashes "page not found" on a successful load.
  if (isPending || data === undefined) return null;

  return (
    <ArticleDetailScreen
      article={{
        title: data.title,
        content: data.content,
        date: data.published_on,
        imageUrl: data.image_url ?? undefined,
        authorName: data.author_name,
        authorAvatarUrl: data.author_avatar_url,
      }}
      backPath="/news/"
      notFoundMessage={t('wbs.page-not-found')}
    />
  );
}

export const Route = createFileRoute('/_app/student-news/$slug')({
  component: StudentNewsPost,
});
